import { api } from '@/plugins/axios'
import event from '@/plugins/events'
import { sentryException } from '@/plugins/sentry'
import { useWebexStore } from '@/stores/webex'
import { useUserStore } from '@/stores/user'
import { useCustomerStore } from '@/stores/customer'
import { useSalesStore } from '@/stores/sales'
import { useZendeskStore } from '@/stores/zendesk'
import { useRouter } from 'vue-router'

// Module-level singletons: the Webex CC SDK is per-agent and must not be
// re-instantiated on every component mount.
let webex = null
let sdkPromise = null
let initPromise = null // dedupes concurrent initWebex() callers (onMounted + click)
let agentId = null // captured from the register profile, for setAgentState
let currentTask = null // the live SDK Task object for the active call (CASH-883).
let outboundPending = false // marks the next tracked task as an outbound (outdial) call, so the bar labels it "Calling…" not "Incoming" (CASH-1093)
// Kept module-side (not in Pinia) so its methods survive; the store mirrors only
// the serializable call snapshot.

// Remote-audio element for a WebRTC/BROWSER call (CASH-1092). Like `currentTask`,
// this is a live, non-serializable DOM object, so it lives module-side — NOT in
// Pinia (a reactive proxy around a media element is trouble) and NOT in a component
// (it must outlive dialog open/close and the call-bar's v-if toggling). One detached
// <audio> element is created lazily and reused for the life of the tab.
let remoteAudio = null

// Lazily create the single <audio> element and return it. `autoplay` so remote audio
// starts the moment a track is attached. Its initial volume is seeded from the store
// so a returning agent keeps their last earpiece level. We never mute the element
// itself — that's the speaker; the mic is muted via task.toggleMute (CASH-1092).
const ensureAudioEl = () => {
  if (remoteAudio) return remoteAudio
  const el = document.createElement('audio')
  el.autoplay = true
  el.volume = useWebexStore().getVolume
  document.body.appendChild(el)
  remoteAudio = el
  return remoteAudio
}

// Attach the SDK's remote MediaStreamTrack to the audio element so call audio plays
// through the agent's headset. A single track is fed to an <audio> element by wrapping
// it in a fresh MediaStream. No-op on a missing track (defensive; the SDK event shape
// is confirmed live in the Phase-6 spike).
const attachRemoteAudio = track => {
  if (!track) return
  ensureAudioEl().srcObject = new MediaStream([track])
}

// Detach on call end / teardown so no dead stream lingers on the element. We keep the
// element itself for reuse and only clear its source.
const detachRemoteAudio = () => {
  if (remoteAudio) remoteAudio.srcObject = null
}

// Confirm microphone access before a BROWSER (WebRTC) login. We open a stream only to
// trigger the permission grant and verify a mic exists, then release it immediately: the
// CC SDK acquires its own capture device when it registers the WebRTC calling session, so
// holding our own capture would double-open the mic. The grant persists for the origin, so
// the SDK's later acquisition is silent. If the Phase-6 spike shows the SDK needs us to
// SUPPLY the stream (LocalMicrophoneStream), this becomes a keep-and-pass. Throws (with a
// DOMException name) on denial / no device / insecure context.
const ensureMicrophoneAccess = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser cannot access the microphone (a secure https connection is required).')
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  stream.getTracks().forEach(t => t.stop()) // release now; the SDK opens its own capture
}

// Map a getUserMedia failure to an agent-facing message; null for a non-mic error so the
// caller falls back to the generic Webex error text. Exported for tests.
export const micErrorMessage = e => {
  const name = e?.name
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'Microphone access is blocked. Allow it in your browser settings, then log in again to take calls in the browser.'
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone was found. Connect a headset or mic, then log in again to take calls in the browser.'
  }
  return null
}

// Reconnect + state-change watchdog state (CASH-1076). Module-level so a setAvailable()
// in one useWebex() closure and the agent:stateChange handler wired in another share the
// same timer — a per-closure var would let the arm and the clear touch different state.
let reconnecting = false // in-flight guard so overlapping tab-wake events don't stack
let stateChangeTimer = null // watchdog: setAgentState only acks the send, not the change
let pendingAgentState = null // 'Available' | 'Idle' — the state awaiting confirmation
// The currently-attached window `unhandledrejection` handler for the fatal Webex Calling
// device-registration error (see wireFatalRegistrationErrorListener below). Module-level so
// a later initWebex() can replace it instead of stacking a duplicate on every reconnect.
let wiredFatalErrorHandler = null

const STATE_CHANGE_TIMEOUT_MS = 8000 // generous, so a slow-but-alive network won't trip it
const clearStateChangeTimer = () => {
  if (stateChangeTimer) clearTimeout(stateChangeTimer)
  stateChangeTimer = null
  pendingAgentState = null
}

// CASH-214 stopgap: the idle/wrap-up codes ARE assigned to the agent's Desktop Profile,
// but stable webex@3.12.0 does not surface them on webex.cc.agentConfig (only the
// -next prerelease does, and that build breaks task.end()). So on stable, register()
// leaves the code lists empty and we seed the pickers from the fallback below.
// The ids MUST be REAL tenant code ids: going Idle / submitting wrap-up sends the id to
// the Webex API, which accepts real ids (confirmed live 2026-07-15: real-id wrap-up -> 202)
// and rejects placeholder ones.
//
// The real codes for the "RD USTS Manual WU" profile are baked in below (STOPGAP_*_CODES)
// so the branch works with no per-dev .env setup — this is a temporary measure. An env var
// (VITE_WEBEX_FALLBACK_IDLE_CODES / VITE_WEBEX_FALLBACK_WRAPUP_CODES, each a JSON array of
// { id, name }) still overrides the defaults for a different agent profile. Remove the
// whole fallback once we are on a stable build that returns the codes on agentConfig.
// Source of these ids: docs/tasks/webex-test-screenshots/auxiliary-code-response.json.
// See docs/tasks/CASH-214-webex-aux-codes-hardcode-stopgap.md.
const STOPGAP_IDLE_CODES = [
  { id: '1c63d638-ee61-40c6-b6e4-87952f06cc18', name: '14-Mentoring' },
  { id: '2852d231-0a3c-4088-a886-daaab277a1b8', name: '9-Outbound' },
  { id: '2d438039-63d3-49e7-bd34-d74808a7a492', name: '2-Break' },
  { id: '33ca0a7c-56a4-488d-b6ab-d9c8605211fb', name: '7-Coaching' },
  { id: '4b6fba83-71c2-4961-b2dc-74367c28ab08', name: '3-Meeting' },
  { id: '51c94e81-51dc-466f-a7c7-8b66e7ee0a85', name: '15-Training Billable' },
  { id: '65204f59-e5ca-4aa4-9686-0d16808551f1', name: '999-dontuse' },
  { id: '76257f33-4a2c-4d57-b256-550911347a19', name: '4-SpecialProjects' },
  { id: '987348db-7471-41c1-a4c2-627cc6ccf921', name: '11-Lunch' },
  { id: 'e7dafdf7-9940-43e6-baa6-121752c04b99', name: '6-Technical' },
]
const STOPGAP_WRAPUP_CODES = [
  { id: '04d43407-d8cb-4738-8b5c-95a87c72138d', name: 'AA_Agent Failed To Code' },
  { id: '0a0224ea-694b-4805-b884-7fdd635fcba7', name: 'RD LiveTech On-Site Sale' },
  { id: '0c60d873-9208-427e-931d-c973fefc8645', name: 'RD - SALE Closed' },
  { id: '0ef85973-36de-434d-8179-0d2e6a1b8409', name: 'RD - SVC Complaint or Escalation' },
  { id: '3bac998b-0865-4479-a466-3f79ac627c33', name: 'RD - SVC Cancelled Competitor' },
  { id: '4c6b121a-dc52-4ca9-bc80-daea872616d9', name: 'RD - Cancellation - Monthly Charge' },
  { id: '5bed6e8b-7f41-4c1c-8c94-1ac7c8345559', name: 'RD - Cancellation - No Support' },
  { id: '5c8b61af-a51f-437d-a55b-2cda21181ca7', name: 'RD - Upgrade or Downgrade Service' },
  { id: '5e92b2f6-c876-4b1f-856a-15aa077cee6a', name: 'RD - Refund - 30 Day Guarantee' },
  { id: '6a98e56e-adf3-4ee1-986a-3c04179d0581', name: 'RD - Other Scam Call - Refund' },
  { id: '6e354c17-ec17-4a2f-bb8c-9f7e5382f0bf', name: 'RD - Cancellation - Too Expensive' },
  { id: '7389fbd9-9304-4bd0-9192-8627ca45d896', name: 'RD - SVC General Product Inquiry' },
  { id: '79a1a329-9a90-4c42-bea0-a6808843f685', name: 'RD - SVC Save No Retention Offer' },
  { id: '809cd079-b11f-4527-b236-7535e4833783', name: 'RD - Refund - Duplicate Order' },
  { id: '868cf2ff-d8b4-474e-9396-b313b6d3166b', name: 'RD - Refund - Customer Satisfaction' },
  { id: '8c617803-43dd-4140-a5b6-fdae568b16f9', name: 'RD - SVC Billing Inquiry' },
  { id: '912245b4-abc8-4ff7-bb91-5af443ed4a43', name: 'RD - SVC Cancelled No Computer or Devices' },
  { id: 'a50fa0fa-113e-4636-808e-dcd094fce15b', name: 'RD - SAVE 60dd Deferment' },
  { id: 'ab0c51e9-848b-4bc0-8b81-4632a3c55ff5', name: 'RD - Cancellation - Slows Computer' },
  { id: 'ab734c39-8a32-491f-8a49-24f08a3ed642', name: 'RD - Other Scam Call - Inquiry' },
  { id: 'be95de68-5dca-44b3-8029-d44d19e8097b', name: 'RD LiveTech Support Call' },
  { id: 'c1c85854-500f-466b-9c0a-8e4fa68e1504', name: 'RD - Cancellation - Software Reputation' },
  { id: 'c9cbae69-c08f-4b25-b98f-3f48400dde27', name: 'RD - SAVE 75 Percent Perpetual Discount' },
  { id: 'ce1ee52b-6be5-44bd-9e92-d57471f407f3', name: 'RD - Renewal Campaign' },
  { id: 'd3110ba1-69d0-4eed-9623-9bade05c695c', name: 'RD - Cancellation Too Hard to Use' },
  { id: 'ef27c403-4090-4d9e-ac57-d1e7b44f0780', name: 'RD - Cancellation Performance Compatable' },
  { id: 'efa999bc-dace-4b5f-87e7-3f21cc6699bf', name: 'RD - SAVE 30dd Deferment' },
  { id: 'f53475c1-c234-4bb5-8dea-d8a7166455ef', name: 'RD - Other Disconnect Prank Ghost' },
  { id: 'f9f163ad-0889-4094-b742-2e26ab989774', name: 'RD - SDC Tech Issue Repeat Issue Ticket' },
]

// Prefer an env override (JSON array of { id, name }), else fall back to the baked-in codes.
const fallbackCodes = (raw, defaults) => {
  if (!raw) return defaults
  try {
    const arr = JSON.parse(raw)
    const parsed = Array.isArray(arr) ? arr.filter(c => c?.id != null && c?.name) : []
    return parsed.length ? parsed : defaults
  } catch {
    return defaults
  }
}

// Pinned Webex CC UMD bundle. Same build for every environment, so this is baked in
// rather than required per-env config; VITE_WEBEX_SDK_URL still overrides it if we ever
// need to point at a different version/mirror without a code change.
//
// Pinned to the 3.12.0-next.202 PRERELEASE (CASH-1092): this is the build carrying Cisco's
// WebRTC (BROWSER) fix — dialNumber + deviceId both sent as webrtc-{agentId}, matching Agent
// Desktop — without which inbound WebRTC calls fail with AgentInviteFailed / INVALID_NUMBER_FORMAT.
// Do NOT drop to stable 3.12.0 (lacks the fix) or 3.12.0-next.165 (breaks task.end()).
// EXIT: pin the STABLE release that folds in this fix once Cisco ships one, and move the SDK
// off this runtime unpkg load to an npm/self-hosted bundle + SRI — tracked in CASH-1601.
const DEFAULT_WEBEX_SDK_URL = 'https://unpkg.com/webex@3.12.0-next.202/umd/webex.min.js'

// Load the Webex CC UMD bundle once (D3). Resolves to the global `Webex`.
const loadSdk = () => {
  if (window.Webex) return Promise.resolve(window.Webex)
  if (sdkPromise) return sdkPromise
  const src = import.meta.env.VITE_WEBEX_SDK_URL || DEFAULT_WEBEX_SDK_URL
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => {
      if (window.Webex) return resolve(window.Webex)
      // Loaded but the global never appeared: clear the cached promise so a later
      // call can retry instead of getting this same rejected promise forever.
      sdkPromise = null
      reject(new Error('Webex SDK loaded but global not found'))
    }
    script.onerror = () => {
      sdkPromise = null
      reject(new Error('Failed to load Webex SDK bundle'))
    }
    document.head.appendChild(script)
  })
  return sdkPromise
}

// Only ever hand window.location.href a URL we can confirm is a Webex consent
// endpoint over https. Guards against a misconfigured WEBEX_OAUTH_BASE or a
// tampered response producing an open redirect.
const isWebexAuthorizeUrl = raw => {
  try {
    const u = new URL(raw)
    return u.protocol === 'https:' && (u.hostname === 'webexapis.com' || u.hostname.endsWith('.webex.com'))
  } catch {
    return false
  }
}

// Detect the "this agent already has a session" (multi-login) conflict. The SDK/back-end
// surface it several ways: errorCode 101 / AGENT_SESSION_ALREADY_EXISTS on register, or
// MultiLoginConfigurationConflict / "Session already exists" on the notification subscribe.
export const isSessionConflict = e => {
  let blob = ''
  try {
    blob = JSON.stringify(e)
  } catch {
    blob = ''
  }
  const haystack = `${e?.reason || ''} ${e?.message || ''} ${e?.errorMessage || ''} ${blob}`
  return (
    e?.errorCode === 101 ||
    /AGENT_SESSION_ALREADY_EXISTS|MultiLoginConfigurationConflict|Session already exists/i.test(haystack)
  )
}

// Turn a raw Webex SDK error into a concise, actionable message for the agent.
// The SDK rejects with structured objects that have no useful `.message`, so surface
// something the agent can act on instead of a generic failure.
export const friendlyWebexError = (e, fallback) => {
  if (isSessionConflict(e)) {
    return "You're already signed into Webex in another session. Sign out of the Webex Agent Desktop (or your other browser tab), then try again."
  }
  return e?.message || fallback
}

// Bounded auto-retry for the multi-login conflict: a prior session (a closed tab, the
// Webex Agent Desktop, or a crashed session) frees on the server's RONA timeout, so
// re-attempt register a few times before surfacing the "sign out elsewhere" message.
const SESSION_CONFLICT_MAX_RETRIES = 8
const SESSION_CONFLICT_RETRY_MS = 15000

// Pull screen-pop fields out of a Webex CC task event. Paths confirmed from the
// Webex CC SDK "Tasks" docs: the event payload (AgentContact) has `interactionId`
// and an `interaction` object whose `callProcessingDetails` holds ani / dnis /
// virtualTeamName. The server owns final normalization (CASH-882).
const extractContactFields = task => {
  const d = task?.data || {}
  const i = d.interaction || {}
  const cpd = i.callProcessingDetails || {}
  let occurred_at = null
  if (d.eventTime) {
    try {
      occurred_at = new Date(d.eventTime).toISOString()
    } catch {
      occurred_at = null
    }
  }
  return {
    session_id: d.interactionId || i.interactionId || null,
    ani: cpd.ani || null,
    dnis: cpd.dnis || null,
    // customer_screen_pops.queue isn't enum-restricted server-side (see screenPopRoute's
    // schema), so the raw Webex virtualTeamName (e.g. Q_USTS_Test) is recorded as-is even
    // though it's a routing queue name, not an intent category like Billing/Tech/Sales.
    queue: cpd.virtualTeamName || null,
    occurred_at, // from AgentContact.eventTime (epoch ms); server also stamps received_at
  }
}

// The serializable call snapshot the UI shows in the control bar (CASH-883).
// Same documented AgentContact paths as extractContactFields, plus customerName.
const extractCallInfo = task => {
  const d = task?.data || {}
  const i = d.interaction || {}
  const cpd = i.callProcessingDetails || {}
  return {
    interactionId: d.interactionId || i.interactionId || null,
    ani: cpd.ani || null,
    dnis: cpd.dnis || null,
    queue: cpd.virtualTeamName || null,
    customerName: cpd.customerName || null,
  }
}

// Record a Webex transfer attempt against the source screen pop (CASH-754) so a
// Webex->Webex handover is reportable even if the far end never answers (no
// destination pop is created; an answering callee shares this pop by ANI).
// Fire-and-forget: no session id means no active call to attribute; a failed
// write is logged and never blocks or fails the live transfer. Exported for tests.
export const recordTransferAttempt = (session_id, to, destinationType) => {
  if (!session_id) return
  api
    .post('/webex/transfer', { session_id, to, destination_type: destinationType })
    .catch(e => event.error(e))
}

// Record an outbound (outdial) attempt against the source screen pop (CASH-1093).
// Fire-and-forget like recordTransferAttempt: no session_id is sent (the outdial is
// placed off-call), so the server attributes it by phone. A failed write never blocks
// the call. Exported for tests.
export const recordOutdialAttempt = to => {
  if (!to) return
  api.post('/webex/outdial', { to }).catch(e => event.error(e))
}

// Normalize a phone-style AGENT_DN dial number for Webex (CASH-1076): strip formatting
// characters (spaces, dashes, parens, dots) and default the country code to +1 (US) when
// the agent didn't include one. A number already starting with + keeps its country code
// and is just cleaned; an 11-digit number starting with 1 is treated as already +1-coded
// (so we never produce +11555…). Empty / non-dialable input is left untouched. Extensions
// are deliberately NOT run through this (a +1 prefix would break an internal extension) —
// stationLogin only calls it for loginOption === 'AGENT_DN'. Exported for tests.
export const normalizeDialNumber = raw => {
  const s = (raw ?? '').trim()
  if (!s) return s
  const digits = s.replace(/\D/g, '')
  if (!digits) return s // nothing dialable; leave whatever was typed
  if (s.startsWith('+')) return `+${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}` // already +1-coded
  return `+1${digits}`
}

export function useWebex() {
  const webexStore = useWebexStore()
  const userStore = useUserStore()
  const customerStore = useCustomerStore()
  const salesStore = useSalesStore()
  const zendeskStore = useZendeskStore()
  const router = useRouter()

  // Handle an inbound contact event by navigating to /search with this leg's own
  // telephony fields as URL params — the existing screen-pop-creation path
  // (processScreenPopFromUrl in SearchForm.vue) owns creating the row, exactly as
  // it does for every other channel (Zendesk links, plain agent search, etc.).
  // The server has no separate Webex-owned row to create or keep in sync: /webex/
  // contact is a pure lookup (does a Retell original for this ANI exist to link
  // to?), so it's only called once the call is actually answered (task:assigned) —
  // searching/popping while the phone is still ringing would look up a customer
  // for a call that might never connect. `react` is true only for that answered
  // event — the initial ringing offer and a hydrate reconnect have nothing left
  // to do here.
  const forwardContact = async (task, react) => {
    if (!react) return
    try {
      const fields = extractContactFields(task)
      if (!fields.session_id) return
      // Only session_id/ani are needed for the lookup; dnis/queue are used below
      // to build the navigation, not sent to the server.
      const { screen_pop_id } = await api.post('/webex/contact', { session_id: fields.session_id, ani: fields.ani })
      const current = router.currentRoute.value
      if (current.name === 'search' && current.query.sessionId === fields.session_id) return
      // session_source tells SearchForm's own "always create a screen pop for these
      // URL params" step (processScreenPopFromUrl) which source to stamp on this
      // leg's row. Without it, that step falls back to the logged-in agent's own
      // session_source ('cash'), so a plain Webex call (no Retell leg to link to)
      // ends up with a 'cash'-labeled row for what is, end to end, a Webex contact.
      // phone/DNIS/queue are carried the same way, for full enrichment, since
      // there's no server-side row left to clone them from.
      const query = { sessionId: fields.session_id, session_source: 'webex' }
      // Only present when a Retell original was found for this ANI — triggers
      // processScreenPopFromUrl's clone-as-new-leg path so this leg inherits the
      // AI's reason_for_contact/ticket_id. Absent otherwise: this leg's own
      // creation is a plain, uncloned row, same as any other channel with no
      // screen_pop_id to link from.
      if (screen_pop_id) query.screen_pop_id = screen_pop_id
      if (fields.ani) query.phone = fields.ani
      if (fields.dnis) query.DNIS = fields.dnis
      if (fields.queue) query.queue = fields.queue
      router.push({ name: 'search', query }).catch(() => {})
    } catch (e) {
      event.error(e)
    }
  }

  // Start the one-time OAuth link: bounce the browser to Webex consent. On
  // return, the user payload carries accounts.webex.linked = true.
  const connect = async () => {
    if (webexStore.isConnecting) return // guard against double-clicks
    webexStore.setError(null)
    webexStore.setConnectionState('connecting')
    try {
      const { url } = await api.get('/webex/oauth/authorize')
      if (!isWebexAuthorizeUrl(url)) throw new Error('Unexpected Webex authorization URL')
      window.location.href = url
    } catch (e) {
      webexStore.setConnectionState('disconnected')
      webexStore.setError(e?.message || 'Unable to start Webex authorization')
      event.error(e)
    }
  }

  // (Re)initialize the SDK with a fresh short-lived access token. Safe to call
  // on reload; no second OAuth redirect because the refresh token lives server-side.
  const initWebex = async () => {
    if (!userStore.getWebexEnabled) return
    if (!userStore.getWebexLinked) return // needs connect() first
    wireFatalRegistrationErrorListener() // (re)bind to this closure's store refs before touching the SDK
    // If an init is already in flight (e.g. onMounted started one and the agent
    // clicks the nav item), await that SAME promise rather than starting a second
    // init or returning early before stations have loaded.
    if (initPromise) return initPromise
    webexStore.setError(null)
    webexStore.setConnectionState('connecting')
    initPromise = (async () => {
      try {
        const { access_token } = await api.post('/webex/oauth/token')
        const Webex = await loadSdk()
        // allowMultiLogin lets a returning/second login take over instead of being
        // rejected with AGENT_SESSION_ALREADY_EXISTS.
        //
        // allowAutomatedRelogin MUST be set to false explicitly (CASH-1092). The SDK
        // deep-merges our config over its defaults (lodash merge in @webex/contact-center
        // webex.js), and the default is TRUE — so simply omitting it does NOT disable the
        // SDK's silent-relogin, contrary to what this code long assumed. Its silent-relogin
        // path (a) reuses the existing session WITHOUT re-fetching the specific idle/wrap-up
        // aux codes, so the pickers come up empty (CASH-883), and (b) for a BROWSER/WebRTC
        // login it redundantly re-registers a second Mobius web calling device on top of the
        // one our own register() already created — Mobius 400s the duplicate
        // (POST /calling/web/device -> 400, "error while performing silentRelogin"),
        // producing the WebRTC registration churn seen in live testing. We drive reconnects
        // ourselves (reconnect(), CASH-1076), so the SDK's automated relogin is pure downside.
        webex = Webex.init({
          credentials: { access_token },
          config: { cc: { allowMultiLogin: true, allowAutomatedRelogin: false } }
        })
        // The SDK initializes asynchronously; its metrics manager (which owns
        // trackEvent) only wires up on `ready`. Calling register()/stationLogin()
        // before that throws "Cannot read properties of undefined (reading
        // 'trackEvent')". Wait for ready, guarding against it having already fired.
        if (!webex.ready) {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Webex SDK ready timed out')), 15000)
            webex.once('ready', () => {
              clearTimeout(timeout)
              resolve()
            })
          })
        }
        // Wire every webex.cc event listener BEFORE calling register() (CASH-1076
        // reconnect-during-a-call race). register() is what makes the CC SDK replay an
        // in-progress call (task:hydrate) or an agent auto-relogin (agent:reloginSuccess),
        // and it can do so as soon as its own promise resolves in our code — or, per an
        // observed live incident, before we ever get a chance to attach a listener at all.
        // These listeners used to be wired up AFTER register() AND after the aux-code poll
        // below (up to ~5s more). That left a window where a mid-call page refresh would
        // have its task:hydrate event fire with nothing listening: the SDK reconnected fine,
        // but the UI never learned the agent was still on a call. Attaching every listener
        // first closes that window regardless of what the SDK fires or when.
        //
        // Inbound contact -> server-owned screen pop (CASH-882). The browser is a
        // thin forwarder: the server creates/dedupes the pop, then we reuse the
        // existing transfer path to pop the customer. Fresh `webex` instance each
        // init, so these subscriptions never stack.
        // Track the live task + call snapshot for the control bar (CASH-883).
        // `offered` = ringing (AGENT_DN rings the phone); `connected` = answered.
        const trackTask = (t, state) => {
          currentTask = t
          webexStore.setCall({ state, ...extractCallInfo(t), outbound: outboundPending })
        }
        // The per-task lifecycle (assigned/hold/resume/end/wrappedup) is emitted on the
        // TASK object, not on webex.cc — confirmed by probe on a live AGENT_DN call:
        // cc only delivers the initial offer (task:incoming/offerContact/hydrate), then
        // the SDK's TaskManager wires the rest on the task itself. So attach our handlers
        // to the task when the offer arrives. WeakSet-guarded so the incoming +
        // offerContact double-fire (same task) doesn't double-wire.
        const wiredTasks = new WeakSet()
        // Full reset for a brand-new contact: mirror the "Clear Customer" nav action
        // (App.vue clearCustomer) so a fresh Webex contact never inherits the
        // previous contact's customer, cart, or Zendesk ticket. Without this, the ticket-
        // creation guards (zendeskStore.getTicketId in SearchForm.vue) see the prior
        // contact's still-loaded ticket and silently reuse it instead of minting a new
        // one. Only for the initial offer (task:incoming/offerContact) — task:hydrate
        // reconnects to an ALREADY in-progress task and must never wipe live state.
        // WeakSet-guarded like wiredTasks so the incoming + offerContact double-fire
        // (same task) can't reset a load that's already in flight.
        const resetTasks = new WeakSet()
        const resetForNewContact = t => {
          if (!t || resetTasks.has(t)) return
          resetTasks.add(t)
          customerStore.clear()
          salesStore.clear()
          zendeskStore.clear()
        }
        const attachTaskHandlers = t => {
          if (!t || wiredTasks.has(t)) return
          wiredTasks.add(t)
          // Answered: flip to connected (this is what makes the controls render), and
          // only now trigger the search/screen-pop — the call must be active first.
          t.on('task:assigned', () => { trackTask(t, 'connected'); forwardContact(t, true) })
          // Hold / resume (confirmed telephony events) — authoritative state alongside
          // the optimistic action update, so an external hold reflects in the bar too.
          t.on('task:hold', () => webexStore.setCallState('held'))
          t.on('task:resume', () => webexStore.setCallState('connected'))
          // WebRTC media (BROWSER login only): the SDK hands us the remote audio track
          // here; route it to the headset and (re)affirm the connected indicator — this
          // also covers the hydrate/reconnect path where stationLogin didn't run this
          // session. Signaling (AGENT_DN/EXTENSION) calls never emit this. Event is wired
          // on the task like the other telephony events; the Phase-6 spike confirms it
          // isn't instead on webex.cc (if so, move this one listener). (CASH-1092)
          t.on('task:media', track => {
            attachRemoteAudio(track)
            webexStore.setMediaReady(true)
          })
          // Ended: task.data.wrapUpRequired decides whether we enter wrap-up (so
          // submitWrapup can act and the picker shows) or clear. autoWrapup seeds the
          // countdown when an auto timer is configured.
          t.on('task:end', () => {
            // Call audio is over: clear the remote stream. mediaReady stays true — the
            // agent is still WebRTC-logged-in and ready for the next call (CASH-1092).
            detachRemoteAudio()
            outboundPending = false // dialing-out phase is over once the task ends, wrap-up or not
            webexStore.setMuted(false) // clear mute on end (wrap-up or not) so it never leaks into the next call
            if (!t?.data?.wrapUpRequired) {
              currentTask = null
              return webexStore.clearCall()
            }
            let autoWrapupSeconds = null
            try {
              if (t?.autoWrapup?.isRunning?.()) autoWrapupSeconds = t.autoWrapup.getTimeLeftSeconds?.() ?? null
            } catch {
              autoWrapupSeconds = null
            }
            const snap = webexStore.getCall || extractCallInfo(t)
            webexStore.setCall({ ...snap, state: 'wrapup', autoWrapupSeconds })
          })
          // Wrap-up completed (agent-submitted or auto-timer): authoritative clear.
          t.on('task:wrappedup', () => {
            currentTask = null
            outboundPending = false
            webexStore.clearCall()
          })
          // Terminal paths that are NOT a normal task:end. An offered task that fails before
          // assignment (e.g. AgentInviteFailed on a WebRTC login) terminates via one of these
          // instead of task:end, so without this the "Incoming · <queue>" bar + Answer button
          // stick forever after the call has dropped. Clear the call on any of them so the UI
          // never shows a phantom contact. clearCall()/detach are idempotent, so this is safe
          // even when a normal end already cleared. (CASH-1092)
          const clearOnTerminate = () => {
            detachRemoteAudio()
            outboundPending = false // an outbound offer can terminate before task:end; don't let the flag leak onto the next task (CASH-1093)
            currentTask = null
            webexStore.clearCall()
          }
          t.on('task:rejected', clearOnTerminate)
          t.on('task:unassigned', clearOnTerminate)
          t.on('task:cleanup', clearOnTerminate)
        }
        // cc delivers the offer; wire the task. task:assigned (on the task) then flips
        // 'offered' -> 'connected' and is what triggers the search/screen-pop, since the
        // call is only actually active once answered. hydrate is the reconnect path
        // where we rejoin an in-progress task.
        webex.cc.on('task:incoming', t => { resetForNewContact(t); trackTask(t, 'offered'); forwardContact(t, false); attachTaskHandlers(t) })
        webex.cc.on('task:offerContact', t => { resetForNewContact(t); trackTask(t, 'offered'); forwardContact(t, false); attachTaskHandlers(t) })
        webex.cc.on('task:hydrate', t => {
          // A reconnect can land on a task that's already in wrap-up (e.g. after a transfer to
          // an entry point / queue, then the caller drops). Reflect that real state instead of
          // forcing 'connected' — otherwise the bar shows an End button for a wrap-up task, and
          // end() returns 202 with no AgentWrapup event, hanging the agent. When in wrap-up we
          // route to the wrap-up picker so the agent can submit a code and the task clears.
          const inWrapup = t?.data?.wrapUpRequired || t?.data?.interaction?.state === 'wrapUp'
          trackTask(t, inWrapup ? 'wrapup' : 'connected')
          forwardContact(t, false)
          attachTaskHandlers(t)
        })
        // Keep the store in sync with server-driven agent state so the UI is honest
        // (CASH-916). agent:stateChange payload: { status: 'LoggedIn'|'LoggedOut',
        // subStatus: 'Available'|'Idle'|..., changedBy, lastStateChangeReason }.
        // logout/multiLogin handlers read fields defensively.
        webex.cc.on('agent:stateChange', payload => {
          if (payload?.status === 'LoggedOut') {
            detachRemoteAudio() // server-driven logout: stop any WebRTC call audio so it can't play on after logout (CASH-1092)
            return webexStore.setLoggedOut()
          }
          if (payload?.subStatus) {
            webexStore.setAvailability(payload.subStatus, payload.lastStateChangeReason)
            // Watchdog (CASH-1076): clear only when the state WE requested is confirmed,
            // so an unrelated server-driven state event can't suppress a needed reconnect.
            if (payload.subStatus === pendingAgentState) clearStateChangeTimer()
          }
        })
        webex.cc.on('agent:logoutSuccess', payload => {
          detachRemoteAudio() // stop any WebRTC call audio on logout (CASH-1092)
          webexStore.setLoggedOut()
          if (payload?.changedBy && payload.changedBy !== 'SELF') {
            const reason = payload?.lastStateChangeReason || payload?.reason
            webexStore.setError(`Webex signed you out${reason ? `: ${reason}` : ''}.`)
          }
        })
        webex.cc.on('agent:reloginSuccess', payload => {
          // Defensive: fires only if the SDK silently relogs into an existing session. We no
          // longer set allowAutomatedRelogin (it suppressed the aux-code fetch, see init), so
          // a reload normally does a fresh register() instead. If a relogin does still occur,
          // treat as station-logged-in and restore availability from the payload's auxCodeId
          // ('0' = Available, any other id = an Idle code — CASH-916 carry-over). Unlike
          // agent:stateChange, this payload has no lastStateChangeReason, so look the code's
          // name up in idleCodes. These listeners are now wired before register()/the aux-code
          // fetch below, so on a very early relogin idleCodes could still be empty — the lookup
          // just falls back to no reason name in that rare case; availability itself is unaffected.
          webexStore.setAgentState(payload || { reloggedIn: true })
          // auxCodeId is empty/null/'0' for Available (confirmed from the SDK log:
          // Available resolves to an empty aux code), or an idle code id for Idle.
          const aux = payload?.auxCodeId
          const isAvailable = !aux || aux === '0'
          const reason = isAvailable ? null : webexStore.getIdleCodes.find(c => c.id === aux)?.name
          webexStore.setAvailability(isAvailable ? 'Available' : 'Idle', reason)
        })
        webex.cc.on('agent:multiLogin', () => {
          detachRemoteAudio() // stop any WebRTC call audio when the session ends elsewhere (CASH-1092)
          webexStore.setLoggedOut()
          webexStore.setError('Your Webex agent signed in elsewhere, so this session ended.')
        })
        // A state change (Available/Idle) failed. setAgentState resolves with no
        // payload, so this event is the only failure signal: surface it rather than
        // leaving a dishonest state in the UI (CASH-916).
        webex.cc.on('agent:stateChangeFailed', err => {
          clearStateChangeTimer() // a real failure arrived; the watchdog's job is done
          webexStore.setError(friendlyWebexError(err, 'Could not change your Webex state.'))
        })

        // register() rejects with a multi-login conflict if the agent's prior session
        // hasn't released yet. That session frees on the server's RONA timeout, so auto-
        // retry a bounded number of times (staying in the "Connecting" state, no alarming
        // error) before giving up and surfacing the "sign out elsewhere" message. Only the
        // session-conflict error is retried; anything else fails fast.
        let profile
        for (let attempt = 0; ; attempt++) {
          try {
            profile = await webex.cc.register()
            break
          } catch (e) {
            if (isSessionConflict(e) && attempt < SESSION_CONFLICT_MAX_RETRIES) {
              webexStore.setConnectionState('connecting')
              await new Promise(r => setTimeout(r, SESSION_CONFLICT_RETRY_MS))
              continue
            }
            throw e
          }
        }
        agentId = profile?.agentId || null
        webexStore.setAgentId(agentId) // durable mirror for the server logout backstop (CASH-1076)
        webexStore.setStations(profile?.teams || [])
        // Allowed login device options for THIS agent's profile (CASH-1092), e.g.
        // ['AGENT_DN', 'EXTENSION', 'BROWSER']. Drives the login-option select and lets
        // BROWSER (WebRTC) default only when the profile actually permits it. Captured
        // alongside stations since both come from the same register() profile and refresh
        // together on every register/reconnect. Fallback to a safe default is a view
        // concern (the dialog), not the store's.
        webexStore.setLoginOptions(profile?.loginVoiceOptions || [])
        // CASH-1092 / Cisco case webex_cc_browser_agentinvitefailed: Cisco asked to see
        // the register() result carrying webRtcEnabled:true and BROWSER in loginVoiceOptions.
        // Log it verbatim so one clean-session capture is self-contained. Diagnostic only —
        // remove once the browser calling-device 403 (POST /api/v1/calling/web/device) is resolved.
        console.info('[CASH webex] register() profile', {
          agentId: profile?.agentId,
          loginVoiceOptions: profile?.loginVoiceOptions,
          webRtcEnabled: profile?.webRtcEnabled,
          isAgentLoggedIn: profile?.isAgentLoggedIn,
        })
        // Aux-code lists for the Status select + wrap-up picker (CASH-883). The real idle
        // and wrap-up codes live on `webex.cc.agentConfig`, NOT on the register() return
        // payload (which only carries the Available default). Confirmed against Cisco's SDK
        // sample for the same agent/token. The catch: the SDK fills agentConfig from two
        // async fetches (agent-profile + auxiliary-code) that can land AFTER register()
        // resolves, so a synchronous read right here can still be empty. Poll briefly until
        // the code lists arrive rather than snapshotting an empty config.
        const readAgentCodes = async () => {
          let last = { idle: [], wrap: [] }
          for (let i = 0; i < 20; i++) { // ~5s max at 250ms
            const c = webex.cc.agentConfig || {}
            last = {
              idle: (c.idleCodes || []).filter(x => x?.id !== '0'), // '0' = Available pseudo
              wrap: c.wrapupCodes || [],
            }
            if (last.idle.length || last.wrap.length) break
            await new Promise(r => setTimeout(r, 250))
          }
          return last
        }
        const { idle: idleReasons, wrap: wrapupCodes } = await readAgentCodes()
        // Fallback for when agentConfig is empty (stable SDK build does not return the
        // SPECIFIC codes — see the init comment and STOPGAP_*_CODES above). Remove the
        // whole fallback once we are on a build that returns them reliably.
        webexStore.setAuxCodes({
          idleCodes: idleReasons.length
            ? idleReasons
            : fallbackCodes(import.meta.env.VITE_WEBEX_FALLBACK_IDLE_CODES, STOPGAP_IDLE_CODES),
          wrapupCodes: wrapupCodes.length
            ? wrapupCodes
            : fallbackCodes(import.meta.env.VITE_WEBEX_FALLBACK_WRAPUP_CODES, STOPGAP_WRAPUP_CODES),
        })
        webexStore.setConnectionState('connected')
        // Already on a station (reload / existing session): reflect logged-in so the
        // agent isn't asked to log in again and we don't fire a redundant stationLogin,
        // which is what throws AGENT_SESSION_ALREADY_EXISTS. Confirmed: register's
        // profile.isAgentLoggedIn is true on reconnect, with the existing dn.
        if (profile?.isAgentLoggedIn) {
          webexStore.setAgentState({ isAgentLoggedIn: true, dn: profile.dn })
        }
        // Station selection is required each session (agents pick it every time),
        // so we do NOT auto-relogin the remembered station here. The remembered
        // choice only pre-selects the dialog. Auto-relogin also fought the SDK's
        // silentRelogin/multiLogin handling and could run after a concurrent
        // disconnect had nulled the SDK.
        return profile
      } catch (e) {
        // A stale client-side `webex_linked` flag (e.g. an admin removed the linked
        // account server-side, or the refresh token was revoked) surfaces here as a
        // 404 'Webex not linked' from /webex/oauth/token. Self-heal instead of leaving
        // the agent stuck on an error with no way back to a login screen: clear the
        // stale flag and restart the one-time OAuth link.
        if (e?.status === 404 && e?.data?.error === 'Webex not linked') {
          userStore.setWebexLinked(false)
          webexStore.setConnectionState('disconnected')
          return connect()
        }
        webexStore.setConnectionState('disconnected')
        webexStore.setError(friendlyWebexError(e, 'Unable to connect to Webex'))
        event.error(e)
      } finally {
        initPromise = null
      }
    })()
    return initPromise
  }

  // Station/team selection is required each session (D2). Persist the choice as
  // the default for next time.
  const stationLogin = async ({ teamId, loginOption, dialNumber } = {}) => {
    if (!webex) throw new Error('Webex SDK not initialized')
    webexStore.setError(null) // clear any prior error so a retry starts clean
    const browser = loginOption === 'BROWSER'
    // BROWSER (WebRTC) routes audio through the tab, so it carries no dial number.
    // AGENT_DN is normalized to +E.164; EXTENSION is left as-is.
    const dn = loginOption === 'AGENT_DN' ? normalizeDialNumber(dialNumber) : dialNumber
    try {
      if (browser) {
        // Secure the mic + prime the audio sink BEFORE registering the WebRTC device, so a
        // permission denial fails the login cleanly instead of half-registering. Creating
        // the <audio> element inside this login click also helps satisfy the browser's
        // autoplay-on-user-gesture policy when call audio later starts.
        await ensureMicrophoneAccess()
        ensureAudioEl()
      }
      const payload = browser ? { teamId, loginOption } : { teamId, loginOption, dialNumber: dn }
      const result = await webex.cc.stationLogin(payload)
      webexStore.setAgentState(result || { teamId, loginOption })
      webexStore.setStation(browser ? { teamId, loginOption } : { teamId, loginOption, dialNumber: dn })
      // WebRTC device registered + mic granted → ready to take browser calls. The per-call
      // audio path is confirmed again by task:media when a call actually connects.
      if (browser) webexStore.setMediaReady(true)
      return result
    } catch (e) {
      // CASH-1092 / Cisco case: log the raw stationLogin() rejection verbatim (Cisco asked
      // for the exact rejection). Diagnostic only — remove with the 403-provisioning fix.
      console.error('[CASH webex] stationLogin rejected', e)
      // A mic-permission/device error gets its own actionable message; anything else falls
      // back to the generic (session-conflict-aware) Webex error text.
      webexStore.setError(micErrorMessage(e) || friendlyWebexError(e, 'Station login failed'))
      event.error(e)
      throw e
    }
  }

  // Station-only logout (CASH-1076 "Change station"): end the current station
  // session but keep the SDK registered/connected so the agent can immediately log
  // into a different station. Unlike disconnect() this does NOT deregister — it uses
  // setLoggedOut() (clears the live station session + availability, but keeps the
  // remembered station selection, connectionState + agentId), leaving a brief
  // "registered, no station" window that changeStation() then fills.
  // Interactive action, so it surfaces errors via the store/event bus like stationLogin.
  const stationLogout = async () => {
    if (!webex) throw new Error('Webex SDK not initialized')
    webexStore.setError(null)
    try {
      // Guard the SDK call on actually being logged in — stationLogout 404s otherwise
      // (same reason disconnect() guards it). Either way, reflect logged-out locally.
      if (webexStore.isStationLoggedIn) await webex.cc.stationLogout({ logoutReason: 'Changing station' })
      detachRemoteAudio() // station gone → stop any WebRTC call audio (CASH-1092)
      webexStore.setLoggedOut()
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Station logout failed'))
      event.error(e)
      throw e
    }
  }

  // Change station (CASH-1076): the SDK rejects a second stationLogin while a station
  // session is active (AGENT_SESSION_ALREADY_EXISTS family), so log out of the current
  // station first, then log into the new one. The SDK stays registered throughout.
  // If stationLogout rejects, the await short-circuits and we never attempt the new
  // login into a still-occupied session; both steps already surface their own errors.
  const changeStation = async (selection = {}) => {
    await stationLogout()
    return stationLogin(selection)
  }

  // Arm the state-change watchdog (CASH-1076). setAgentState only acks the send; the real
  // outcome arrives later on agent:stateChange/agent:stateChangeFailed. Over a dead socket
  // those never come and the UI silently sticks. Arm BEFORE dispatch so a setAgentState
  // that hangs still trips the timer. On timeout, re-check the store first — a confirmation
  // can race in after the timer has already elapsed (clearTimeout can't unqueue it) — and
  // only then surface an error and reconnect. Never optimistically flip the state.
  const armStateChangeTimer = requested => {
    clearStateChangeTimer()
    pendingAgentState = requested
    stateChangeTimer = setTimeout(() => {
      stateChangeTimer = null
      const settled = webexStore.getAvailability === pendingAgentState
      pendingAgentState = null
      if (settled) return // a confirmation raced in as the timer fired — nothing is stuck
      // For a WebRTC/BROWSER station, do NOT reconnect() — its deregister would drop the live
      // calling device and spawn the registration conflict spiral (CASH-1092). Surface the
      // stall non-destructively; the agent can retry the status change. (reconnect() is also
      // self-guarded against BROWSER, but keep this message honest.)
      if (webexStore.isBrowserLogin) {
        webexStore.setError("Webex didn't confirm your status change — try again.")
        return
      }
      webexStore.setError("Webex isn't responding — reconnecting…")
      reconnect()
    }, STATE_CHANGE_TIMEOUT_MS)
  }

  // Set the agent Available so the router can offer contacts (an inbound call only
  // reaches the agent when Available). setAgentState ('0' = Available) returns no
  // payload; the real outcome arrives via agent:stateChange / agent:stateChangeFailed,
  // so availability is NOT set optimistically here: the event handlers own it so the
  // UI never shows Available when the change actually failed (CASH-916).
  const setAvailable = async () => {
    if (!webex) throw new Error('Webex SDK not initialized')
    webexStore.setError(null)
    armStateChangeTimer('Available') // watch for the confirmation before dispatching
    try {
      const payload = { state: 'Available', auxCodeId: '0' }
      if (agentId) payload.agentId = agentId
      return await webex.cc.setAgentState(payload)
    } catch (e) {
      clearStateChangeTimer() // the request itself failed; no confirmation event is coming
      webexStore.setError(friendlyWebexError(e, 'Could not set Available'))
      event.error(e)
      throw e
    }
  }

  // Set the agent Idle (paused, not receiving contacts) with a reason aux code
  // (CASH-883). auxCodeId comes from the Idle picker (store.getIdleCodes). Like
  // setAvailable, availability is NOT set optimistically: setAgentState returns no
  // payload, so agent:stateChange / agent:stateChangeFailed own the result (CASH-916).
  const setIdle = async ({ auxCodeId, reason } = {}) => {
    if (!webex) throw new Error('Webex SDK not initialized')
    webexStore.setError(null)
    armStateChangeTimer('Idle') // watch for the confirmation before dispatching
    try {
      const payload = { state: 'Idle', auxCodeId }
      if (agentId) payload.agentId = agentId
      if (reason) payload.lastStateChangeReason = reason
      return await webex.cc.setAgentState(payload)
    } catch (e) {
      clearStateChangeTimer() // the request itself failed; no confirmation event is coming
      webexStore.setError(friendlyWebexError(e, 'Could not set Idle'))
      event.error(e)
      throw e
    }
  }

  // Re-establish a fresh SDK session after the socket dies silently (a frozen/slept
  // background tab — CASH-1076). Re-registers via initWebex (which restores the station
  // from profile.isAgentLoggedIn) but deliberately does NOT auto-set Available: the agent
  // re-chooses when ready, so recovery never makes someone silently reachable. Guarded so
  // it no-ops when Webex isn't in use or while already reconnecting.
  //
  // hasActiveCall normally blocks this (never disrupt live call audio/signaling), but that
  // guard becomes a trap after a LONG suspend: a laptop-sleep-length gap kills the
  // websocket/task the same as any other suspend, so a `call` snapshot that predates a long
  // sleep is stale, not live, and would otherwise block reconnect() forever — the exact
  // "acts connected, never recovers, needs a full CASH logout" bug (CASH-1299 follow-up).
  // suspendedMs (elapsed hidden time, passed by the caller's wake handler) lets us tell a
  // real live call (short/no gap) from a call snapshot that can't possibly still be live.
  const STALE_CALL_SUSPEND_MS = 60000 // 1 minute+ hidden: no real signaling channel survives this
  const reconnect = async (suspendedMs = 0) => {
    if (!userStore.getWebexEnabled || !userStore.getWebexLinked) return
    // A WebRTC/BROWSER station registers a live Mobius calling device; reconnect()'s
    // deregister() tears it down, and the re-register races the not-yet-released device into
    // AGENT_SESSION_ALREADY_EXISTS / 400 / 429 churn (observed live, CASH-1092). The dead-
    // socket recovery this was built for (CASH-1076, signaling) is the wrong medicine for
    // WebRTC, so skip it entirely (before any call-state check): the SDK's own calling-line
    // keep-alive maintains the media registration, and a genuinely dead session is recovered
    // by a manual re-login.
    if (webexStore.isBrowserLogin) return
    if (webexStore.hasActiveCall) {
      if (suspendedMs < STALE_CALL_SUSPEND_MS) return // could be a genuinely live call — never disrupt it
      // Long suspend: the recorded call can't have survived it. Drop the stale snapshot
      // so recovery isn't blocked forever, and tell the agent why in case the call really
      // did drop out from under them.
      sentryException(new Error('Webex reconnect: clearing stale call snapshot after long tab suspend'))
      webexStore.clearCall()
      // A toast (not just store.error) because initWebex() below unconditionally clears
      // store.error at the start of its own attempt — a store-only message would vanish
      // before the agent ever saw it once the reconnect succeeds.
      event.warning('Webex reconnected after your browser was asleep. Please verify any in-progress call.')
    }
    if (reconnecting) return // in-flight guard: overlapping wake events don't stack
    reconnecting = true
    clearStateChangeTimer()
    try {
      detachRemoteAudio() // drop any stale stream before re-registering (never mid-call — guarded above)
      try {
        if (webex?.cc) await webex.cc.deregister()
      } catch (e) {
        sentryException(e) // the stale socket may already be dead; best-effort
      }
      webex = null
      initPromise = null
      webexStore.setConnectionState('disconnected')
      await initWebex() // re-registers a fresh socket and restores the station
    } finally {
      reconnecting = false
    }
  }

  // --- Active-call controls (CASH-883), all signaling-only (no WebRTC audio) ---
  // Each acts on the module-level currentTask. end() lets the task:end event drive
  // the state transition (clear or wrap-up); hold/resume update optimistically on
  // success for instant feedback and are reconciled by the confirmed task:hold /
  // task:resume events.

  const endCall = async () => {
    if (!currentTask) throw new Error('No active Webex call')
    webexStore.setError(null)
    try {
      return await currentTask.end()
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Could not end the call'))
      event.error(e)
      throw e
    }
  }

  // --- WebRTC/BROWSER call controls (CASH-1092) ---
  // Only meaningful for a browser-audio call; the UI shows these only when
  // webexStore.isBrowserLogin. answerCall accepts an in-browser offer (a phone-login
  // offer rings the physical phone instead); toggleMute mutes the agent's MIC (not the
  // earpiece); volumeUp/Down adjust the earpiece via the audio element + store.

  // Accept an incoming WebRTC contact in the browser. No-op-safe for auto-answer tenants,
  // where task:assigned arrives without an explicit accept() and this is simply unused.
  const answerCall = async () => {
    if (!currentTask) throw new Error('No active Webex call')
    webexStore.setError(null)
    try {
      return await currentTask.accept()
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Could not answer the call'))
      event.error(e)
      throw e
    }
  }

  // Mute/unmute the agent's microphone. task.toggleMute() is a signaling control
  // (confirmed on the live task), so it works on an AGENT_DN leg too (CASH-1093), not just a
  // WebRTC/BROWSER call. The SDK call returns no new state, so mirror it optimistically into
  // the store (like hold/resume); task:end/reset clears it for the next call.
  const toggleMute = async () => {
    if (!currentTask) throw new Error('No active Webex call')
    webexStore.setError(null)
    try {
      const res = await currentTask.toggleMute()
      webexStore.setMuted(!webexStore.isMuted)
      return res
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Could not mute/unmute'))
      event.error(e)
      throw e
    }
  }

  // Earpiece volume. The store clamps [0,1] + persists (the invariant boundary); we then
  // read the CLAMPED value back and apply it to the live audio element, so pressing Up at
  // max (or Down at 0) is a no-op instead of drifting the element past the range.
  const VOLUME_STEP = 0.1
  const setCallVolume = v => {
    webexStore.setVolume(v)
    if (remoteAudio) remoteAudio.volume = webexStore.getVolume
  }
  const volumeUp = () => setCallVolume(webexStore.getVolume + VOLUME_STEP)
  const volumeDown = () => setCallVolume(webexStore.getVolume - VOLUME_STEP)

  const holdCall = async () => {
    if (!currentTask) throw new Error('No active Webex call')
    webexStore.setError(null)
    try {
      const res = await currentTask.hold()
      webexStore.setCallState('held')
      return res
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Could not hold the call'))
      event.error(e)
      throw e
    }
  }

  const resumeCall = async () => {
    if (!currentTask) throw new Error('No active Webex call')
    webexStore.setError(null)
    try {
      const res = await currentTask.resume()
      webexStore.setCallState('connected')
      return res
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Could not resume the call'))
      event.error(e)
      throw e
    }
  }

  // Submit wrap-up for the just-ended call. wrapUpReason/auxCodeId come from the
  // wrap-up picker (store.getWrapupCodes). Clears the call on success. Arg key
  // names per the SDK wrapup() signature (pending final doc confirmation).
  const submitWrapup = async ({ auxCodeId, wrapUpReason } = {}) => {
    if (!currentTask) throw new Error('No active Webex call to wrap up')
    webexStore.setError(null)
    try {
      const res = await currentTask.wrapup({ wrapUpReason, auxCodeId })
      currentTask = null
      webexStore.clearCall()
      return res
    } catch (e) {
      webexStore.setError(friendlyWebexError(e, 'Could not submit wrap-up'))
      event.error(e)
      throw e
    }
  }

  // Transfer targets (CASH-883). getQueues lists routable queues; getBuddyAgents
  // lists other agents for a direct transfer (mediaType 'telephony' = voice, per
  // the Buddy Agents API; state optional: Available | Idle | both). Payload shapes
  // are validated live and normalized in CallControlBar.vue.
  const getQueues = async () => {
    if (!webex) throw new Error('Webex SDK not initialized')
    return webex.cc.getQueues()
  }

  // Entry points (CASH-1095): transfer targets like the post-call survey. Mirrors
  // getQueues — raw SDK passthrough, normalized in CallControlBar.vue. Takes the SDK's
  // default page; if a tenant ever exceeds one page of entry points we'd need to paginate.
  const getEntryPoints = async () => {
    if (!webex) throw new Error('Webex SDK not initialized')
    return webex.cc.getEntryPoints()
  }

  const getBuddyAgents = async state => {
    if (!webex) throw new Error('Webex SDK not initialized')
    const payload = { mediaType: 'telephony' }
    if (state) payload.state = state
    return webex.cc.getBuddyAgents(payload)
  }

  // Transfer the active call. destinationType: 'dialNumber' | 'queue' | 'agent' |
  // 'entrypointDialNumber'; `to` is the number / queueId / agentId / entryPointId respectively.
  // Signaling only.
  //
  // We deliberately do NOT block the UI on the SDK's transfer promise. Webex rejects a blind
  // transfer after a ~15s timeout when the AgentBlindTransferred confirmation event never
  // arrives — even though the transfer SUCCEEDED (HTTP 202) and the contact routed to its
  // destination. The rejection carries no usable signal to tell that benign case apart from a
  // real failure (confirmed live on CASH-1095: reasonCode 0, empty errorData/trackingId). The
  // task lifecycle is authoritative instead: a successful transfer moves the agent task to
  // wrap-up (task:end fires within ~1-2s, well before the transfer timeout); a genuine failure
  // leaves the agent on the live call. So fire the transfer, return immediately (the dialog
  // closes without a 15s spinner), and reconcile in the background — surface an error ONLY if
  // the agent is still on a live call.
  const transferCall = async ({ to, destinationType } = {}) => {
    if (!currentTask) throw new Error('No active Webex call')
    if (!to || !destinationType) throw new Error('Transfer needs a destination')
    // Capture the interaction id before the transfer: task:end/wrapup may null currentTask
    // once the transfer completes.
    const session_id = extractContactFields(currentTask)?.session_id
    webexStore.setError(null)
    currentTask
      .transfer({ to, destinationType })
      .then(() => recordTransferAttempt(session_id, to, destinationType))
      .catch(e => {
        // Still on a live (non-wrap-up) call = the transfer genuinely didn't take: surface it.
        const call = webexStore.getCall
        if (call && call.state !== 'wrapup') {
          webexStore.setError(friendlyWebexError(e, 'Could not transfer the call'))
          event.error(e)
          return
        }
        // Task already left the agent (wrap-up / cleared): the transfer took and the SDK simply
        // never got its confirmation event. Record the attempt and swallow the benign timeout.
        recordTransferAttempt(session_id, to, destinationType)
        sentryException(e)
      })
  }

  // Place an outbound call (CASH-1093). AGENT_DN: the agent's phone rings first,
  // then the destination is dialed. startOutdial takes the destination only —
  // origin is omitted so the backend uses the agent's configured outdial ANI
  // (passing an explicit ANI 400s; confirmed live on stable 3.12.0). The outbound
  // task flows through the same task:offerContact/assigned handlers as an inbound
  // call, so outboundPending flags it for the bar's "Calling…" label.
  const startOutdial = async number => {
    if (!webex) throw new Error('Webex SDK not initialized')
    webexStore.setError(null)
    const destination = normalizeDialNumber(number)
    if (!destination) throw new Error('Invalid outdial number')
    try {
      outboundPending = true
      const res = await webex.cc.startOutdial(destination)
      recordOutdialAttempt(destination)
      return res
    } catch (e) {
      outboundPending = false
      webexStore.setError(friendlyWebexError(e, 'Could not start outdial'))
      event.error(e)
      throw e
    }
  }

  // Tear down the SDK session (best-effort) and clear state. Called on CASH
  // logout so an agent who leaves CASH stops being a live Webex agent. Fuller
  // deregister hardening lands with the call-controls ticket.
  //
  // This never ends/wraps up an in-progress call/task on Cisco's side — it only
  // deregisters the agent. A voluntary logout (NavigationDrawer's nav click) is
  // blocked upstream while webexStore.hasActiveCall, specifically so this function
  // is never reached mid-call; a forced logout (session-expired 401) still reaches
  // here regardless, so the Cisco-side task can survive it and hydrate back on the
  // agent's next login.
  const disconnect = async () => {
    // Only call SDK teardown when actually in that state. Calling stationLogout
    // when not logged in 404s, and deregister when not registered throws inside
    // the SDK (reads webRtcEnabled off an undefined profile).
    // Both calls stay best-effort (never block/throw out of logout — the agent is
    // leaving CASH regardless), but a failed stationLogout leaves the agent live on
    // the Webex CC server past this CASH session ending, so capture it silently
    // (Sentry only, no user-facing toast mid-logout) instead of swallowing it.

    // Server-side force-logout backstop (CASH-1076): fire-and-forget (never delay logout)
    // and independent of SDK state, so a dead socket or a null `webex` (tab sleep) still
    // ends the agent's Webex CC session server-side. Reads agentId synchronously before
    // reset() clears it below. Only for linked Webex users, so a non-Webex agent's logout
    // never hits the endpoint; when agentId is unknown (SDK never registered this session)
    // the server still targets the agent from the stored refresh-token identity. Rejections
    // go to Sentry.
    if (userStore.getWebexEnabled && userStore.getWebexLinked) {
      api.post('/webex/logout', { agent_id: webexStore.getAgentId }).catch(sentryException)
    }

    try {
      if (webex?.cc && webexStore.isStationLoggedIn) await webex.cc.stationLogout({ logoutReason: 'Agent logged out of CASH' })
    } catch (e) {
      sentryException(e)
    }
    try {
      if (webex?.cc && webexStore.isConnected) await webex.cc.deregister()
    } catch (e) {
      sentryException(e)
    }
    detachRemoteAudio() // release the WebRTC audio stream on full teardown (CASH-1092)
    webex = null
    currentTask = null
    outboundPending = false // don't let a mid-attempt outdial flag leak past logout/reconnect
    initPromise = null
    clearStateChangeTimer()
    webexStore.reset()
  }

  // The Webex Calling SDK's WebRTC device/line registration is fully private to
  // @webex/contact-center's internal WebCallingService (confirmed against the SDK source:
  // it listens for LINE_EVENTS.REGISTERED/UNREGISTERED/INCOMING_CALL but never
  // LINE_EVENTS.ERROR), so a fatal registration failure — an expired token, or Mobius
  // returning 404 for a device that went stale over an overnight gap (observed live
  // 2026-07-31) — has no public webex.cc event to listen for. It only ever surfaces as an
  // uncaught promise rejection on window, ending in "Sign out, then sign back in" (the SDK's
  // own wording for both of its fatal registration errors — the only ones worded that way).
  // That rejection is the sole hook available, so catch it there and act on the SDK's own
  // advice: force the agent through a fresh login/station pick rather than leaving them
  // stuck on a session whose device can never come back on its own.
  const wireFatalRegistrationErrorListener = () => {
    if (wiredFatalErrorHandler) window.removeEventListener('unhandledrejection', wiredFatalErrorHandler)
    wiredFatalErrorHandler = async event => {
      const message = event?.reason?.message
      if (typeof message !== 'string' || !/Sign out, then sign back in/i.test(message)) return
      event.preventDefault() // we ARE handling this; don't let the browser log it as uncaught
      sentryException(event.reason)
      // Never yank a live call's UI out from under the agent — the SDK's own device is
      // already unusable by this point, so there's nothing left to protect except the
      // in-progress screen pop / call state. The agent signs back in once the call ends.
      if (webexStore.hasActiveCall) {
        webexStore.setError('Webex Calling lost its device registration. Sign out and back in once this call ends.')
        return
      }
      // disconnect() ends with webexStore.reset(), which clears `error` back to null — set
      // the message AFTER it settles so it's what the agent actually sees, not something
      // reset() immediately wipes.
      await disconnect()
      webexStore.setError('Webex signed you out to refresh its device registration. Please log back in.')
    }
    window.addEventListener('unhandledrejection', wiredFatalErrorHandler)
  }

  return {
    connect,
    initWebex,
    stationLogin,
    stationLogout,
    changeStation,
    reconnect,
    setAvailable,
    setIdle,
    endCall,
    holdCall,
    resumeCall,
    answerCall,
    toggleMute,
    volumeUp,
    volumeDown,
    submitWrapup,
    getQueues,
    getEntryPoints,
    getBuddyAgents,
    transferCall,
    startOutdial,
    disconnect
  }
}
